import _ from 'lodash';
import { PersistenceLevel, Store } from '@/core/flux.service';
import { SearchSuggestion } from '@/homescreen/search/omniSearch.types';

export interface OmniSearchState {
  query: string;
  isLoading: boolean;
  results: SearchSuggestion[];
  totalResults: number;
  pageNumber: number;
  pageSize: number;
}

export interface OmniSearchDehydratedState {
  pageSize: number;
}

export class OmniSearchStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKBENCH';
  static readonly storeName = 'sqOmniSearchStore';

  initialize() {
    this.state = this.immutable({
      query: '',
      isLoading: false,
      results: [],
      totalResults: 0,
      pageNumber: 1,
      pageSize: 15,
    });
  }

  get query(): string {
    return this.state.get('query');
  }

  get isLoading(): boolean {
    return this.state.get('isLoading');
  }

  get results(): SearchSuggestion[] {
    return this.state.get('results');
  }

  get totalResults(): number {
    return this.state.get('totalResults');
  }

  get pageNumber(): number {
    return this.state.get('pageNumber');
  }

  get pageSize(): number {
    return this.state.get('pageSize');
  }

  protected readonly handlers = {
    SET_SEARCH_QUERY: ({ query }: { query: string }) => {
      this.state.set('query', query);
    },
    SET_SEARCH_LOADING: ({ isLoading }: { isLoading: boolean }) => {
      this.state.set('isLoading', isLoading);
    },
    SET_SEARCH_RESULTS: ({ results, totalResults }: { results?: SearchSuggestion[]; totalResults?: number }) => {
      this.state.set('results', results || []);
      this.state.set('totalResults', totalResults || 0);
    },
    SET_SEARCH_PAGE_NUMBER: ({ pageNumber }: { pageNumber: number }) => {
      this.state.set('pageNumber', pageNumber);
    },
    SET_SEARCH_PAGE_SIZE: ({ pageSize }: { pageSize: number }) => {
      this.state.set('pageSize', pageSize);
    },
    CLEAR_SEARCH_RESULTS: () => {
      this.state.set('results', []);
      this.state.set('totalResults', 0);
      this.state.set('pageNumber', 1);
    },
  };

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated state.
   */
  dehydrate() {
    return _.pick(this.state.serialize(), ['pageSize']);
  }

  /**
   * Re-creates the workbench state.
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   * @return {Object} A promise that is fulfilled when the workbench is completely rehydrated.
   */
  rehydrate(dehydratedState: Partial<OmniSearchDehydratedState>) {
    this.state.merge(dehydratedState);
  }
}
